<template>
  <div
    class="property-view-page pt-1"
    style="padding-top: 80px"
    v-if="property"
  >
    <section class="property-heading my-4 md:my-8">
      <div class="container mx-auto">
        <div class="grid grid-cols-2 md:grid-cols-7 items-center p-4">
          <div class="md:col-span-5">
            <h2>{{ property.city.name }}</h2>
            <p class="capitalize">
              {{ property.property_type.classification }} -
              {{ property.property_type.name }}
            </p>
          </div>
          <div class="md:col-span-2 text-right">
            <div class="price">
              <h2>
                &euro;{{ property.price }}
                <small class="font-normal"> monthly</small>
              </h2>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="property-images my-4 md:my-8 p-4">
      <vue-gallery-slideshow
        :images="propertyImages"
        :index="showingImageIndex"
        @close="showingImageIndex = null"
      ></vue-gallery-slideshow>
      <div class="container mx-auto" v-if="property.photos.length">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-6">
          <div class="col-span-2 pb-4 md:pb-0">
            <div
              class="image-container"
              @click="showingImageIndex = currentImage"
            >
              <img
                :src="`${baseImageUrl}/storage/${property.photos[currentImage].image}`"
                alt="1.jpg"
              />
            </div>
          </div>
          <div class="col-span-1">
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="(photo, index) in property.photos.slice(0, 7)"
                :key="index"
                class="image-container"
                @click="currentImage = index"
              >
                <img
                  :src="`${baseImageUrl}/storage/${photo.image}`"
                  alt="1.jpg"
                />
              </div>
              <div class="image-container more" @click="showingImageIndex = 0">
                <p class="m-0 col-start-auto">+ 4</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-background-light py-16 text-black">
      <div class="container mx-auto px-8">
        <p class="text-center font-bold text-lg">
          Reference: {{ property.ref_no }}
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 md:gap-6 mt-8">
          <div>
            <div class="mb-3">
              <p class="mb-2">Full Name</p>
              <input type="text" placeholder="Full Name" />
            </div>
            <div class="mb-3">
              <p class="mb-2">Email</p>
              <input type="text" placeholder="Email" />
            </div>
            <div class="mb-3 md:mb-0">
              <p class="mb-2">Contact Number</p>
              <input type="text" placeholder="Contact Number" />
            </div>
          </div>
          <div>
            <div class="mb-5">
              <p class="mb-2">Message</p>
              <textarea
                name="message"
                id="message"
                placeholder="Let us know how we can help"
                cols="30"
                rows="7"
              ></textarea>
            </div>
            <button
              type="submit"
              class="btn btn-primary w-full"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="property-details my-4 md:my-8 p-4">
      <div class="container mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 lg:gap-6">
          <div class="col-span-4">
            <div class="p-6 border border-gray-400 rounded-lg mb-6">
              <div class="tags mb-6">
                <ul>
                  <li>{{ property.property_type.name }}</li>
                  <li>Beds: {{ property.bedrooms }}</li>
                  <li>
                    Baths: {{ property.main_bathrooms + property.ensuites }}
                  </li>
                </ul>
              </div>
              <h4 class="mb-4">Description</h4>
              <p>
                {{ property.description }}
              </p>
            </div>

            <div class="p-6 border border-gray-400 rounded-lg mb-6">
              <h4 class="mb-8">Property Details</h4>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 gap-y-4">
                <div class="grid grid-cols-2">
                  <p>Property ID :</p>
                  <p class="font-bold">{{ property.ref_no }}</p>
                </div>
                <div class="grid grid-cols-2">
                  <p>Price :</p>
                  <p class="font-bold">&euro;{{ property.price }} monthly</p>
                </div>
                <div class="grid grid-cols-2" v-if="property.floor_area">
                  <p>Size :</p>
                  <p class="font-bold">{{ property.floor_area }} sqm</p>
                </div>
                <div class="grid grid-cols-2">
                  <p>Bedrooms :</p>
                  <p class="font-bold">{{ property.bedrooms }}</p>
                </div>
                <div class="grid grid-cols-2">
                  <p>Main Bathrooms :</p>
                  <p class="font-bold">{{ property.main_bathrooms }}</p>
                </div>
                <div class="grid grid-cols-2">
                  <p>Ensuites :</p>
                  <p class="font-bold">{{ property.ensuites }}</p>
                </div>
                <div class="grid grid-cols-2">
                  <p>Type :</p>
                  <p class="font-bold">{{ property.property_type.name }}</p>
                </div>
                <div class="grid grid-cols-2">
                  <p>Status :</p>
                  <p class="font-bold">
                    {{ isAvailable ? "Available" : "Coming Soon" }}
                  </p>
                </div>
              </div>
            </div>

            <div class="p-6 border border-gray-400 rounded-lg mb-6">
              <h4 class="mb-8">Property Amenities</h4>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-4 gap-y-4">
                <div
                  class="text-primary-color flex items-center"
                  v-for="(amenity, index) in property.amenities"
                  :key="index"
                >
                  <font-awesome-icon :icon="['fas', 'check']" class="mr-4" />
                  <p class="text-black">{{ amenity.amenity.name }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-span-2">
            <div class="px-6 pt-6 border border-gray-400 rounded-lg">
              <h4 class="title mb-6">Featured Properties</h4>
              <div
                class="
                  grid grid-cols-1
                  md:grid-cols-2
                  lg:grid-cols-1
                  gap-x-6
                  lg:gap-0
                "
              >
                <FeaturedPropertySidebar
                  v-for="(property, index) in featuredProperties
                    .filter((x) => x.photos.length)
                    .slice(0, 3)"
                  :key="`featuredProperty-${index}`"
                  :property="property"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { FeaturedPropertySidebar } from "@/components/atoms";
import { LISTING_TYPES } from "@/constants";

import VueGallerySlideshow from "vue-gallery-slideshow";

export default {
  props: ["id"],
  components: {
    FeaturedPropertySidebar,
    VueGallerySlideshow,
  },
  data: function () {
    return {
      LISTING_TYPES,
      baseImageUrl: process.env.VUE_APP_API_URL,
      currentImage: 0,
      showingImageIndex: null,
    };
  },
  computed: {
    ...mapGetters("Properties", {
      propertyDetails: "propertyDetails",
      featuredProperties: "featuredProperties",
    }),
    property() {
      return this.propertyDetails(this.id);
    },
    isAvailable() {
      return new Date() >= new Date(this.property.availability_date);
    },
    propertyImages() {
      return this.property?.photos.map((photo) => {
        return `${this.baseImageUrl}/storage/${photo.image}`;
      });
    },
  },
  watch: {
    id: function (value) {
      this.getPropertyDetails(value);

      if (!this.featuredProperties.length) {
        this.getFeaturedProperties();
      }
    },
  },
  methods: {
    ...mapActions("Properties", {
      getPropertyDetails: "getPropertyDetails",
      getFeaturedProperties: "getFeaturedProperties",
    }),
  },
  beforeMount: function () {
    this.getPropertyDetails(this.id);

    if (!this.featuredProperties.length) {
      this.getFeaturedProperties();
    }
  },
};
</script>

<style scoped lang="scss">
.tags {
  @apply text-white;

  ul li {
    @apply inline-block bg-gray-300 px-4 py-2 rounded-md mr-2 text-gray-800 font-medium;
  }
}
input[type="radio"],
input[type="checkbox"] {
  width: 15px;
  height: 15px;
}
</style>
